import { FC, useState } from 'react';
import { Box, Button, Chip, CircularProgress, Popover, Typography } from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import { Beispiel, ladeBeispiele } from './feldpruefungApi';
import { datumDe } from './texte';

/**
 * „Beispiele“ neben einem Feld: zeigt, was bei früheren Webinaren drinstand (gleiches Produkt zuerst), mit Quelle
 * und Datum. „Übernehmen“ schreibt nur ins Feld — gespeichert wird erst mit dem normalen Speichern-Knopf.
 */
const BeispieleKnopf: FC<{
  feld: string;
  webinarId: string | null | undefined;
  onUebernehmen: (wert: string) => void;
  /** Kurzer Hinweis über der Liste, z. B. „Preise und Fristen bitte prüfen“. */
  hinweis?: string;
}> = ({ feld, webinarId, onUebernehmen, hinweis }) => {
  const [anker, setAnker] = useState<HTMLElement | null>(null);
  const [liste, setListe] = useState<Beispiel[] | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laedt, setLaedt] = useState(false);

  if (!webinarId) return null;

  const oeffnen = async (el: HTMLElement) => {
    setAnker(el);
    if (liste) return;
    setLaedt(true);
    const r = await ladeBeispiele(feld, webinarId);
    setLaedt(false);
    if (r.success) setListe(r.data ?? []);
    else setFehler(r.error ?? 'Beispiele konnten nicht geladen werden');
  };

  return (
    <>
      <Button
        size="small"
        variant="text"
        startIcon={<HistoryIcon sx={{ fontSize: 16 }} />}
        onClick={(e) => void oeffnen(e.currentTarget)}
        sx={{ textTransform: 'none', fontSize: 12, py: 0, minHeight: 0 }}
      >
        Beispiele
      </Button>
      <Popover
        open={!!anker}
        anchorEl={anker}
        onClose={() => setAnker(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ paper: { sx: { width: 460, maxWidth: '92vw', p: 1.5 } } }}
      >
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
          So stand es bei früheren Webinaren
        </Typography>
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
          {hinweis ??
            'Nur als Vorschlag. Übernehmen füllt das Feld, gespeichert wird erst mit „Speichern“.'}
        </Typography>
        {laedt && <CircularProgress size={18} />}
        {fehler && (
          <Typography variant="body2" color="error">
            {fehler}
          </Typography>
        )}
        {liste && liste.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            Noch keine Beispiele vorhanden.
          </Typography>
        )}
        {liste?.map((b, i) => (
          <Box
            key={`${b.webinarId}-${i}`}
            sx={{ border: '1px solid #e5e7eb', borderRadius: 1, p: 1, mb: 1, bgcolor: '#fafafa' }}
          >
            <Typography
              variant="body2"
              sx={{ whiteSpace: 'pre-line', maxHeight: 120, overflow: 'auto', mb: 0.5 }}
            >
              {b.wert}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="caption" color="text.secondary" sx={{ flex: 1, minWidth: 0 }}>
                {b.titel}
                {b.datum ? ` · ${datumDe(b.datum)}` : ''}
              </Typography>
              {b.gleichesProdukt && (
                <Chip size="small" label="gleiches Produkt" sx={{ height: 18, fontSize: 10 }} />
              )}
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  onUebernehmen(b.wert);
                  setAnker(null);
                }}
                sx={{ textTransform: 'none', py: 0 }}
              >
                Übernehmen
              </Button>
            </Box>
          </Box>
        ))}
      </Popover>
    </>
  );
};

export default BeispieleKnopf;
